import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  FormHelperText,
  CircularProgress,
  Box,
  Divider,
  Typography,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from '../../../redux/store';
import EventsApi from '../../../api/eventsApi/eventsApi';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose }) => {
  const authEmail = useSelector(state => state.auth.email) || '';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [passwordSuccess, setPasswordSuccess] = useState<string>('');

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!minLength) return 'Şifre en az 8 karakter uzunluğunda olmalıdır';
    if (!hasUpperCase) return 'Şifre en az 1 büyük harf içermelidir';
    if (!hasLowerCase) return 'Şifre en az 1 küçük harf içermelidir';
    if (!hasSpecialChar) return 'Şifre en az 1 özel karakter içermelidir';
    return '';
  };

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      setLoading(true);
      setError('');
      setSuccess('');
      setPasswordError('');
      setPasswordSuccess('');
      setActiveTab('profile');
      try {
        const user = await EventsApi.getUser();
        setFirstName(user.firstName ?? '');
        setLastName(user.lastName ?? '');
        setEmail(user.email ?? authEmail ?? '');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } catch (e) {
        console.error('Profile load error:', e);
        setEmail(authEmail ?? '');
        setError('Profil bilgileri yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, authEmail]);

  useEffect(() => {
    if (!open) return;

    // Only validate when user starts typing
    if (!newPassword && !confirmPassword && !oldPassword) {
      setPasswordError('');
      return;
    }

    const validationResult = validatePassword(newPassword);
    if (validationResult) {
      setPasswordError(validationResult);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Şifreler eşleşmiyor');
      return;
    }

    setPasswordError('');
  }, [open, oldPassword, newPassword, confirmPassword]);

  const handleSubmitProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (!firstName.trim() || !lastName.trim()) {
        setError('Lütfen ad ve soyad alanlarını doldurun.');
        return;
      }

      await EventsApi.updateUserFromAuth({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      setSuccess('Profil bilgileri güncellendi.');
    } catch (e) {
      console.error('Profile update error:', e);
      setError('Profil güncelleme işlemi başarısız oldu.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      if (!email?.trim()) {
        setPasswordError('E-posta bilgisi bulunamadı. Lütfen tekrar giriş yapın.');
        return;
      }
      if (!oldPassword) {
        setPasswordError('Lütfen mevcut şifrenizi girin.');
        return;
      }
      if (!newPassword || !confirmPassword) {
        setPasswordError('Lütfen yeni şifre alanlarını doldurun.');
        return;
      }

      const validationResult = validatePassword(newPassword);
      if (validationResult) {
        setPasswordError(validationResult);
        return;
      }

      if (newPassword !== confirmPassword) {
        setPasswordError('Şifreler eşleşmiyor');
        return;
      }

      await EventsApi.changePassword({
        email: email.trim(),
        oldPassword,
        newPassword,
      });

      setPasswordSuccess('Şifre başarıyla değiştirildi.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      console.error('Password change error:', e);
      setPasswordError('Şifre değiştirme işlemi başarısız oldu.');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <span>Ayarlar</span>
        <IconButton aria-label="Kapat" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ mb: 2 }}
            >
              <Tab
                value="profile"
                icon={<PersonOutlineIcon />}
                iconPosition="start"
                label="Profil"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
              <Tab
                value="password"
                icon={<LockOutlinedIcon />}
                iconPosition="start"
                label="Şifre"
                sx={{ textTransform: 'none', fontWeight: 600 }}
              />
            </Tabs>

            <Divider sx={{ mb: 2 }} />

            {activeTab === 'profile' && (
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Profil Bilgileri</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Ad ve soyad bilgilerinizi buradan güncelleyebilirsiniz.
                </Typography>

                <form onSubmit={handleSubmitProfile}>
                  <TextField
                    fullWidth
                    label="Ad"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Soyad"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="E-posta"
                    value={email}
                    disabled
                    helperText="E-posta bu ekrandan değiştirilemez."
                    sx={{ mb: 1 }}
                  />

                  {error && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {error}
                    </FormHelperText>
                  )}
                  {success && (
                    <FormHelperText sx={{ mt: 1, color: 'success.main' }}>
                      {success}
                    </FormHelperText>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      sx={{
                        backgroundColor: '#094151',
                        color: "#fff",
                        '&:hover': {
                          backgroundColor: '#022631',
                          color: "#fff",
                          fontWeight: 600,
                        },
                        '&:disabled': {
                          backgroundColor: '#094151',
                          opacity: 0.5,
                          color: "#fff"
                        }
                      }}
                      type="submit"
                      disabled={saving}
                    >
                      {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}

            {activeTab === 'password' && (
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Şifre Değiştir</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Güvenliğiniz için güçlü bir şifre belirleyin.
                </Typography>

                <form onSubmit={handleSubmitPassword}>
                  <TextField
                    fullWidth
                    label="Mevcut Şifre"
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Yeni Şifre"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    sx={{ mb: 2 }}
                    error={!!passwordError && newPassword.length > 0}
                  />
                  <TextField
                    fullWidth
                    label="Yeni Şifre Tekrar"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={!!passwordError && confirmPassword.length > 0}
                  />

                  {passwordError && (newPassword.length > 0 || confirmPassword.length > 0 || oldPassword.length > 0) && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {passwordError}
                    </FormHelperText>
                  )}
                  {passwordSuccess && (
                    <FormHelperText sx={{ mt: 1, color: 'success.main' }}>
                      {passwordSuccess}
                    </FormHelperText>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      sx={{
                        backgroundColor: '#094151',
                        color: "#fff",
                        '&:hover': {
                          backgroundColor: '#022631',
                          color: "#fff",
                          fontWeight: 600,
                        },
                        '&:disabled': {
                          backgroundColor: '#094151',
                          opacity: 0.5,
                          color: "#fff"
                        }
                      }}
                      type="submit"
                      disabled={passwordSaving || !!passwordError || !oldPassword || !newPassword || !confirmPassword}
                    >
                      {passwordSaving ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
                    </Button>
                  </Box>
                </form>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal; 